/*
 * ExperienceReportPanel — v2.7 lines 655-686 auto-generated report.
 *
 * Surfaces at L2 completion as a full-screen overlay invoked from the
 * Scope page rail. Seven sections + three forward paths. User reviews,
 * picks a path, lands back on the Scope page with the engagement
 * routing_decision.forward_path stamped.
 *
 * Brand: white surface, brand-blue accent on chosen path, IBM Plex
 * Sans, sentence case, no emoji. Sections lay out as cards with section
 * letter / number pill in the header (matches the Brief tab pattern).
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  X, Loader2, Building2, Target, Compass, Wrench, Rocket, Layers, Calendar,
  ChevronRight, ExternalLink, Sparkles, AlertTriangle,
} from 'lucide-react';
import clsx from 'clsx';

import {
  fetchExperienceReport, pickForwardPath,
  type ExperienceReportResponse, type ExperienceReportForwardPath,
} from '../lib/scopeApi';


interface ExperienceReportPanelProps {
  engagementId: string;
  /** 'overlay' = full-screen fixed-inset modal with close button.
   *  'page' = renders inline so it can be mounted inside the dashboard
   *  Layout (with the sidebar visible). Page mode hides the close X
   *  since navigation handles "back to my-business". */
  variant?: 'overlay' | 'page';
  onClose?: () => void;
  onPathPicked?: (path: ExperienceReportForwardPath['id']) => void;
}


export const ExperienceReportPanel: React.FC<ExperienceReportPanelProps> = ({
  engagementId, variant = 'overlay', onClose, onPathPicked,
}) => {
  const [report, setReport] = useState<ExperienceReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState<ExperienceReportForwardPath['id'] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchExperienceReport(engagementId);
        if (!cancelled) setReport(r);
      } catch (e) {
        console.error('[experience-report] load failed', e);
        if (!cancelled) setError("Couldn't load the report. Try refreshing.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [engagementId]);

  useEffect(() => {
    if (variant !== 'overlay' || !onClose) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, variant]);

  const onPickPath = useCallback(async (path: ExperienceReportForwardPath['id']) => {
    if (picking) return;
    setPicking(path);
    try {
      await pickForwardPath(engagementId, path);
      onPathPicked?.(path);
      onClose?.();
    } catch (e) {
      console.error('[experience-report] pick path failed', e);
      setError("Couldn't save your pick. Try again.");
      setPicking(null);
    }
  }, [picking, engagementId, onPathPicked, onClose]);

  // Layout chrome differs per variant:
  //  • overlay → fixed inset-0, top bar with close X (modal feel)
  //  • page    → renders inline so the dashboard Layout sidebar stays
  //              visible; identity header is slimmer, no close X.
  const isOverlay = variant === 'overlay';
  const containerClass = isOverlay
    ? 'fixed inset-0 z-[120] flex flex-col bg-white font-sans'
    : 'flex min-h-[calc(100vh-32px)] flex-col font-sans';

  return (
    <div className={containerClass}>
      {/* Top bar */}
      <header className={clsx(
        'flex flex-none items-center justify-between border-b border-gray-200 px-6 py-4',
        isOverlay ? 'bg-white' : 'bg-white rounded-t-xl',
      )}>
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-brand-50 text-brand-500">
            <Sparkles size={16} strokeWidth={2.25} />
          </div>
          <div>
            <h1 className="text-base font-semibold text-gray-900">Your scoping summary</h1>
            <p className="text-xs text-gray-500">What we scoped, and what to build next.</p>
          </div>
        </div>
        {isOverlay && onClose && (
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-md text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        )}
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[1100px] px-6 py-8">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 size={14} className="animate-spin" />
              Composing your report.
            </div>
          )}
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
          {report && !error && (
            <>
              {!report.ready && (
                <NotReadyBanner
                  problemCount={report.problem_count}
                  scopedCount={report.scoped_count}
                />
              )}

              {report.burned_upsell && <BurnedUpsellBanner />}

              {/* §K strategic outcome — auto-drafted, framing the report */}
              <SectionKOutcomes data={report.sections.k_outcomes} />

              <Section1 data={report.sections['1_where_you_are']} />
              <Section2 proposals={report.sections['2_proposals']} />
              <Section3 data={report.sections['3_recommended_techniques']} />
              <Section4 data={report.sections['4_recommended_tools']} />
              <Section5 agents={report.sections['5_scoped_agents']} />
              <Section6 packs={report.sections['6_skill_packs']} />
              <Section7 plan={report.sections['7_action_plan']} />
              {/* §P success scorecard — closes the report before forward paths */}
              <SectionPScorecard data={report.sections.p_scorecard} />

              <ForwardPathsSection
                paths={report.forward_paths}
                picking={picking}
                onPick={onPickPath}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExperienceReportPanel;


// ── Section building blocks ───────────────────────────────────────────

const SectionShell: React.FC<{
  number: number;
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ number, title, icon, children }) => (
  // Brand surface: cool off-white #eef3ff (brand-50). Hairline ring
  // instead of a heavier border. Section number uses brand-blue mono
  // pill per the AA "01 / 02 / 03 — zero-padded, mono, in --secondary
  // blue" convention.
  <section className="mb-6 rounded-xl bg-brand-50 p-5 ring-1 ring-gray-200/60">
    <header className="mb-3 flex items-center gap-2.5 border-b border-gray-200/50 pb-2.5">
      <span className="font-sans text-[11px] font-semibold tracking-[0.14em] text-brand-500">
        {String(number).padStart(2, '0')}
      </span>
      <span className="text-brand-500/80">{icon}</span>
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
    </header>
    {children}
  </section>
);


const NotReadyBanner: React.FC<{ problemCount: number; scopedCount: number }> = ({ problemCount, scopedCount }) => (
  // Soft brand-blue notice. The user-facing copy drops the §2/§5 jargon
  // and the parenthetical count for a cleaner read.
  <div className="mb-6 flex items-start gap-3 rounded-xl bg-brand-50 p-4 ring-1 ring-brand-500/20">
    <AlertTriangle size={18} className="mt-0.5 flex-none text-brand-600" />
    <div>
      <h3 className="text-sm font-semibold text-gray-900">Report is partial.</h3>
      <p className="mt-0.5 text-xs leading-relaxed text-gray-700">
        {scopedCount === 0
          ? `No problems scoped yet. Add a problem on the Scope page and Orbit will help you scope it. ${problemCount} drafted so far.`
          : `${scopedCount} of ${problemCount} problems scoped. Finish scoping the rest and the proposals + scoped agents sections will fill in.`}
      </p>
    </div>
  </div>
);


const BurnedUpsellBanner: React.FC = () => {
  const bookingUrl = (process.env.REACT_APP_CONSULTANT_CAL_URL
    || 'https://cal.com/team-brndsh/review-session');
  return (
    // On-brand: brand-50 surface, brand-blue accent CTA + hairline ring.
    // No purple — the brand palette uses purple sparingly for chart data,
    // not for upsell banners.
    <div className="mb-6 flex flex-col items-start gap-3 rounded-xl bg-brand-50 p-4 ring-1 ring-brand-500/20 md:flex-row md:items-center md:justify-between">
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold text-gray-900">
          Want a human to review this?
        </h3>
        <p className="mt-0.5 text-xs leading-relaxed text-gray-700">
          Your prior AI investment didn't fully pay off. A Solution partner can
          walk through this report with you. Single session, no commitment.
        </p>
      </div>
      <a
        href={bookingUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex flex-none items-center gap-1.5 rounded-md bg-brand-500 px-4 py-2 text-xs font-medium text-white transition-all hover:bg-brand-600 hover:-translate-y-px hover:shadow-[0_4px_20px_rgba(24,25,29,0.25)] whitespace-nowrap"
      >
        Book a review session
        <ExternalLink size={11} strokeWidth={2.5} />
      </a>
    </div>
  );
};


// ── §K Strategic outcomes (auto-drafted) ─────────────────────────────

const KShell: React.FC<{
  letter: string;
  title: string;
  icon: React.ReactNode;
  autoDrafted: boolean;
  children: React.ReactNode;
}> = ({ letter, title, icon, autoDrafted, children }) => (
  // §K is the editorial north-star moment. Per brand: "Marketing can go
  // full dark on alternating sections." True black surface, no border
  // (the surface color does the work), accent-yellow pill for the §K
  // counter to echo the brand's Pulse-yellow editorial highlight.
  <section className="mb-6 rounded-xl bg-black p-5 text-white">
    <header className="mb-3 flex items-center gap-2 border-b border-white/10 pb-2.5">
      <span className="rounded-md bg-accent-500 px-2 py-0.5 font-sans text-[10px] uppercase tracking-wider text-gray-900">
        §{letter}
      </span>
      <span className="text-white/70">{icon}</span>
      <h2 className="text-sm font-semibold">{title}</h2>
      {autoDrafted && (
        <span className="ml-auto rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white/70">
          Auto-drafted
        </span>
      )}
    </header>
    {children}
  </section>
);

const SectionKOutcomes: React.FC<{
  data: ExperienceReportResponse['sections']['k_outcomes'];
}> = ({ data }) => (
  <KShell
    letter="K"
    title="Strategic outcome"
    icon={<Target size={15} strokeWidth={2} />}
    autoDrafted={data.auto_drafted}
  >
    <p className="mb-3 text-base font-semibold leading-snug">{data.primary_outcome}</p>
    <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
      <DarkStat label="Baseline" value={data.baseline || '—'} />
      <DarkStat label="Target" value={data.target || '—'} />
      <DarkStat label="Deadline" value={data.deadline || '—'} />
    </div>
    {data.counter_metrics.length > 0 && (
      <div className="mt-3 border-t border-white/15 pt-3">
        <h4 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-white/60">
          Don't break.
        </h4>
        <ul className="flex flex-wrap gap-1.5">
          {data.counter_metrics.map((m, i) => (
            <li key={i} className="rounded-md bg-white/10 px-2 py-0.5 text-[11px] text-white/90">
              {m}
            </li>
          ))}
        </ul>
      </div>
    )}
  </KShell>
);

const DarkStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-lg bg-white/5 px-3 py-2">
    <div className="text-[10px] font-medium uppercase tracking-wider text-white/50">{label}</div>
    <div className="mt-0.5 text-sm font-medium text-white">{value}</div>
  </div>
);


// ── §P Success scorecard (auto-drafted) ──────────────────────────────

const SectionPScorecard: React.FC<{
  data: ExperienceReportResponse['sections']['p_scorecard'];
}> = ({ data }) => (
  // Brand surface (no heavy 2px border). Hairline ring matches the rest
  // of the report; brand-blue mono pill on the §P counter matches §1-§7.
  <section className="mb-6 rounded-xl bg-brand-50 p-5 ring-1 ring-gray-200/60">
    <header className="mb-3 flex items-center gap-2 border-b border-gray-200/50 pb-2.5">
      <span className="rounded-md bg-brand-500 px-2 py-0.5 font-sans text-[10px] uppercase tracking-wider text-white">
        §P
      </span>
      <span className="text-gray-500">
        <Sparkles size={15} strokeWidth={2} />
      </span>
      <h2 className="text-sm font-semibold text-gray-900">Success scorecard</h2>
      {data.auto_drafted && (
        <span className="ml-auto rounded-full bg-brand-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-brand-700">
          Auto-drafted
        </span>
      )}
    </header>
    {data.master_metric && (
      <div className="mb-3 rounded-lg bg-white p-3 ring-1 ring-gray-200/60">
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Master metric</h4>
        <p className="mt-1 text-sm font-semibold text-gray-900">{data.master_metric}</p>
      </div>
    )}
    <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
      <MilestoneCard label="30 days" body={data.milestone_30} />
      <MilestoneCard label="60 days" body={data.milestone_60} />
      <MilestoneCard label="90 days" body={data.milestone_90} />
    </div>
    {data.failure_thresholds.length > 0 && (
      <div className="mt-3 rounded-lg bg-white p-3 ring-1 ring-gray-200/60">
        <h4 className="mb-1.5 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
          <AlertTriangle size={11} strokeWidth={2.25} className="text-gray-400" />
          Failure thresholds
        </h4>
        <ul className="flex flex-col gap-1">
          {data.failure_thresholds.map((t, i) => (
            <li key={i} className="text-[11px] leading-relaxed text-gray-700">
              {t}
            </li>
          ))}
        </ul>
      </div>
    )}
    <div className="mt-3 flex items-baseline gap-1.5 text-[11px] text-gray-600">
      <span className="font-semibold uppercase tracking-wider text-gray-500">Owner</span>
      <span className="text-gray-900">{data.measurement_owner}</span>
    </div>
  </section>
);

const MilestoneCard: React.FC<{ label: string; body: string }> = ({ label, body }) => (
  <div className="rounded-lg border border-brand-200 bg-white p-3">
    <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-brand-600">{label}</h4>
    <p className="text-[12px] leading-relaxed text-gray-800">{body}</p>
  </div>
);


// ── §1 Where You Are ─────────────────────────────────────────────────

// Per AA chart order — yellow → blue → green → (purple → red, unused
// here since maturity tops out at five). Cyan/blue-400 aren't tokens.
const STAGE_META: Record<string, { color: string; idx: number }> = {
  starting:      { color: 'bg-gray-300',  idx: 0 },
  exploring:     { color: 'bg-accent-500', idx: 1 },
  experimenting: { color: 'bg-brand-400', idx: 2 },
  building:      { color: 'bg-brand-500', idx: 3 },
  scaling:       { color: 'bg-green-500', idx: 4 },
};

const Section1: React.FC<{ data: ExperienceReportResponse['sections']['1_where_you_are'] }> = ({ data }) => {
  const stages: Array<keyof typeof STAGE_META> = ['starting', 'exploring', 'experimenting', 'building', 'scaling'];
  const activeIdx = STAGE_META[data.maturity_stage]?.idx ?? 1;
  return (
    <SectionShell number={1} title="Where you are" icon={<Building2 size={15} strokeWidth={2} />}>
      <p className="mb-3 text-sm leading-relaxed text-gray-700">{data.readiness_summary}</p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Maturity" value={data.maturity_label} />
        <Stat label="Posture" value={data.adoption_posture || 'unset'} />
        <Stat label="Size" value={data.company_size || 'unset'} />
        <Stat label="Industry" value={data.industry || 'unset'} />
      </div>
      {/* 5-stage maturity strip */}
      <div className="mt-4 flex items-center gap-1.5">
        {stages.map((s, i) => (
          <div key={s} className="flex flex-1 flex-col items-center gap-1">
            <div className={clsx(
              'h-1.5 w-full rounded-full transition-all',
              i <= activeIdx ? STAGE_META[s].color : 'bg-gray-200',
            )} />
            <span className={clsx(
              'text-[10px] uppercase tracking-wide',
              i === activeIdx ? 'font-semibold text-gray-900' : 'text-gray-400',
            )}>{s}</span>
          </div>
        ))}
      </div>
    </SectionShell>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
    <div className="text-[10px] font-medium uppercase tracking-wider text-gray-500">{label}</div>
    <div className="mt-0.5 text-sm font-semibold capitalize text-gray-900">{value}</div>
  </div>
);


// ── §2 Proposals ─────────────────────────────────────────────────────

const Section2: React.FC<{ proposals: ExperienceReportResponse['sections']['2_proposals'] }> = ({ proposals }) => (
  <SectionShell number={2} title="Proposals" icon={<Target size={15} strokeWidth={2} />}>
    {proposals.length === 0 ? (
      <p className="font-serif text-sm italic text-gray-500">No scoped problems yet. Scope at least one problem to see proposals here.</p>
    ) : (
      <div className="flex flex-col gap-2">
        {proposals.map((p, i) => (
          <div key={p.problem_id} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-sm font-semibold text-gray-900">
                <span className="mr-1.5 inline-block rounded bg-gray-200 px-1.5 py-0.5 font-sans text-[10px] text-gray-700">
                  #{i + 1}
                </span>
                {p.statement || 'Untitled'}
              </h3>
              {p.priority && (
                <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-600">
                  {p.priority}
                </span>
              )}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-2 text-[11px] text-gray-600">
              {p.approach && <span className="rounded bg-brand-50 px-1.5 py-0.5 text-brand-700">{p.approach}</span>}
              {p.success_metric && <span>Metric: <strong>{p.success_metric}</strong></span>}
              {p.target && <span>Target: <strong>{p.target}</strong></span>}
              {p.deadline && <span>Deadline: <strong>{p.deadline}</strong></span>}
            </div>
          </div>
        ))}
      </div>
    )}
  </SectionShell>
);


// ── §3 Recommended Techniques (per affected dept) ────────────────────

// Per AA: no per-card color identity for items that read as siblings.
// All technique cards share the canonical brand-50 surface; the
// technique itself is conveyed by the label pill, not by hue. The two
// semantically loaded states keep their colors (green = good shipping
// signal, accent-yellow = editorial highlight on the recommended path).
const TECHNIQUE_META: Record<string, { color: string; label: string }> = {
  guided_training:    { color: 'bg-brand-50 text-gray-900 ring-1 ring-gray-200/60',  label: 'Training-first' },
  augmentation_first: { color: 'bg-brand-50 text-gray-900 ring-1 ring-gray-200/60',  label: 'Augment first' },
  champion_led_pilot: { color: 'bg-brand-50 text-gray-900 ring-1 ring-brand-500/30', label: 'Pilot first' },
  automation_forward: { color: 'bg-brand-50 text-gray-900 ring-1 ring-green-500/30', label: 'Automate now' },
  standard_rollout:   { color: 'bg-brand-50 text-gray-900 ring-1 ring-gray-200/60',  label: 'Standard rollout' },
};

const Section3: React.FC<{ data: ExperienceReportResponse['sections']['3_recommended_techniques'] }> = ({ data }) => (
  <SectionShell number={3} title="Recommended techniques" icon={<Compass size={15} strokeWidth={2} />}>
    {data.placeholder || data.items.length === 0 ? (
      <p className="font-serif text-[11px] italic leading-relaxed text-gray-500">
        {data.note}
      </p>
    ) : (
      <>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {data.items.map(it => {
            const meta = TECHNIQUE_META[it.technique] || TECHNIQUE_META.standard_rollout;
            return (
              <div
                key={it.department}
                className={`flex flex-col gap-1 rounded-lg p-3 ${meta.color}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-semibold capitalize">{it.department}</span>
                  <span className="rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide">
                    {meta.label}
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-900">{it.headline}</p>
                <p className="text-[11px] leading-relaxed text-gray-700">{it.detail}</p>
                <p className="mt-1 text-[10px] text-gray-500">
                  {it.problem_count} problem{it.problem_count === 1 ? '' : 's'} touch this dept.
                </p>
              </div>
            );
          })}
        </div>
        <p className="mt-3 font-serif text-[10px] italic leading-relaxed text-gray-400">{data.note}</p>
      </>
    )}
  </SectionShell>
);


// ── §4 Recommended Tools (leverage · new · retire) ───────────────────

const Section4: React.FC<{ data: ExperienceReportResponse['sections']['4_recommended_tools'] }> = ({ data }) => {
  const empty =
    data.leverage_existing.length === 0 &&
    data.new_connections.length === 0 &&
    data.retire_candidates.length === 0;

  return (
    <SectionShell number={4} title="Recommended tools" icon={<Wrench size={15} strokeWidth={2} />}>
      {empty ? (
        <p className="font-serif text-[11px] italic leading-relaxed text-gray-500">{data.note}</p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <ToolBucket
              title="Leverage existing"
              tone="bg-brand-50 ring-1 ring-green-500/30 text-gray-900"
              emptyLabel="No overlap yet."
              items={data.leverage_existing.map(t => ({
                label: t.tool,
                meta: `${t.workflow_count} workflow${t.workflow_count === 1 ? '' : 's'}`,
              }))}
            />
            <ToolBucket
              title="New connections"
              tone="bg-brand-50 ring-1 ring-brand-500/30 text-gray-900"
              emptyLabel="No new tools yet."
              items={data.new_connections.map(t => ({
                label: t.tool,
                meta: `${t.workflow_count} workflow${t.workflow_count === 1 ? '' : 's'}`,
              }))}
            />
            <ToolBucket
              title="Retire candidates"
              tone="bg-brand-50 ring-1 ring-accent-500/40 text-gray-900"
              emptyLabel="No shelfware flagged."
              items={data.retire_candidates.map(t => ({ label: t.name, meta: null }))}
            />
          </div>
          <p className="mt-3 font-serif text-[10px] italic leading-relaxed text-gray-400">{data.note}</p>
        </>
      )}
    </SectionShell>
  );
};

const ToolBucket: React.FC<{
  title: string;
  tone: string;
  emptyLabel: string;
  items: Array<{ label: string; meta: string | null }>;
}> = ({ title, tone, emptyLabel, items }) => (
  <div className={`flex flex-col gap-1.5 rounded-lg p-3 ${tone}`}>
    <h4 className="text-[10px] font-semibold uppercase tracking-wider">{title}</h4>
    {items.length === 0 ? (
      <p className="font-serif text-[11px] italic opacity-70">{emptyLabel}</p>
    ) : (
      <ul className="flex flex-col gap-1">
        {items.map((it, i) => (
          <li
            key={`${it.label}-${i}`}
            className="flex items-center justify-between rounded bg-white/80 px-2 py-1 text-[11px]"
          >
            <span className="truncate font-medium capitalize text-gray-900">{it.label}</span>
            {it.meta && <span className="flex-none text-[10px] text-gray-500">{it.meta}</span>}
          </li>
        ))}
      </ul>
    )}
  </div>
);


// ── §5 Scoped agents ─────────────────────────────────────────────────

const Section5: React.FC<{ agents: ExperienceReportResponse['sections']['5_scoped_agents'] }> = ({ agents }) => {
  const validxBase = (process.env.REACT_APP_VALIDX_URL || '').replace(/\/$/, '');
  return (
    <SectionShell number={5} title="Scoped agents (results)" icon={<Rocket size={15} strokeWidth={2} />}>
      {agents.length === 0 ? (
        <p className="font-serif text-sm italic text-gray-500">No agents scoped yet. Pick a Match Engine option on a scoped problem to see PRDs here.</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {agents.map(a => (
            <div key={a.problem_id} className="flex flex-col gap-1.5 rounded-lg border border-gray-200 bg-white p-3">
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="line-clamp-2 flex-1 text-sm font-semibold text-gray-900">{a.problem_statement}</h3>
                <span className="flex-none rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-medium text-brand-700">
                  {a.kind_label}
                </span>
              </div>
              <div className="flex items-center justify-between text-[11px] text-gray-500">
                <span>{a.prd_drafted ? 'PRD drafted' : a.status === 'PRD_READY' ? 'PRD drafting…' : 'Ready to draft'}</span>
                {a.validx_project_id && validxBase && (
                  <a
                    href={`${validxBase}/customer/agent/${a.validx_project_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 font-medium text-brand-600 hover:underline"
                  >
                    Open workspace
                    <ExternalLink size={10} strokeWidth={2.5} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionShell>
  );
};


// ── §6 Skill packs ───────────────────────────────────────────────────

const Section6: React.FC<{ packs: ExperienceReportResponse['sections']['6_skill_packs'] }> = ({ packs }) => (
  <SectionShell number={6} title="Skill packs / plugins" icon={<Layers size={15} strokeWidth={2} />}>
    {packs.length === 0 ? (
      <p className="font-serif text-[11px] italic leading-relaxed text-gray-500">
        Department-level skill packs surface here when a problem's solution hypothesis = skill_pack. No matches yet.
      </p>
    ) : (
      <ul className="flex flex-col gap-2">
        {packs.map(p => (
          <li key={p.problem_id} className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm">
            <div className="font-medium text-gray-900">{p.statement}</div>
            {p.affected_departments.length > 0 && (
              <div className="mt-0.5 flex flex-wrap gap-1.5 text-[11px] text-gray-600">
                {p.affected_departments.map(d => (
                  <span key={d} className="rounded bg-white px-1.5 py-0.5 text-gray-700">{d}</span>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    )}
  </SectionShell>
);


// ── §7 Action plan ───────────────────────────────────────────────────

const Section7: React.FC<{ plan: ExperienceReportResponse['sections']['7_action_plan'] }> = ({ plan }) => (
  <SectionShell number={7} title="Action plan" icon={<Calendar size={15} strokeWidth={2} />}>
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      <ActionBucket label="30 days" items={plan.days_30} />
      <ActionBucket label="60 days" items={plan.days_60} />
      <ActionBucket label="90 days" items={plan.days_90} />
    </div>
  </SectionShell>
);

const ActionBucket: React.FC<{
  label: string;
  items: ExperienceReportResponse['sections']['7_action_plan']['days_30'];
}> = ({ label, items }) => (
  <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</h4>
    {items.length === 0 ? (
      <p className="font-serif text-[11px] italic text-gray-400">Nothing scheduled here yet.</p>
    ) : (
      <ul className="flex flex-col gap-2">
        {items.map(it => (
          <li key={it.problem_id} className="text-[11px] leading-relaxed">
            <div className="font-medium text-gray-900 line-clamp-1">{it.statement}</div>
            <div className="text-gray-600">{it.milestone}</div>
          </li>
        ))}
      </ul>
    )}
  </div>
);


// ── Forward paths ────────────────────────────────────────────────────

// Both lanes share the brand-blue hover treatment — per AA brand kit
// we avoid per-lane color identities (no purple/amber accents on what
// are effectively two equal-weight choices).
const PATH_ACCENT: Record<ExperienceReportForwardPath['id'], string> = {
  build:    'hover:bg-brand-50',
  training: 'hover:bg-brand-50',
};

const ForwardPathsSection: React.FC<{
  paths: ExperienceReportForwardPath[];
  picking: ExperienceReportForwardPath['id'] | null;
  onPick: (id: ExperienceReportForwardPath['id']) => void;
}> = ({ paths, picking, onPick }) => (
  // Brand-50 surface + 1px ring per AA — borders are never thicker than
  // 1px, and the left-edge color-stripe pattern is explicitly avoided.
  <section className="mt-8 rounded-xl bg-brand-50 p-6 ring-1 ring-gray-200/60">
    <header className="mb-4">
      <h2 className="text-base font-semibold text-gray-900">Pick your forward path.</h2>
      <p className="mt-0.5 text-xs text-gray-600">
        How do you want to move forward? Build, train, or both. You can change this later.
      </p>
    </header>
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {paths.map(p => (
        <button
          key={p.id}
          type="button"
          onClick={() => onPick(p.id)}
          disabled={!!picking}
          className={clsx(
            'flex flex-col items-start gap-2 rounded-lg bg-white p-4 text-left ring-1 ring-gray-200/70 transition-all hover:-translate-y-0.5 hover:ring-brand-500/40 hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)]',
            PATH_ACCENT[p.id],
            picking && picking !== p.id && 'opacity-50',
            picking ? 'cursor-not-allowed' : 'cursor-pointer',
          )}
        >
          <h3 className="text-sm font-semibold text-gray-900">{p.label}</h3>
          <p className="text-xs leading-relaxed text-gray-700">{p.tagline}</p>
          <p className="text-[11px] leading-relaxed text-gray-500">{p.detail}</p>
          <div className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-brand-600">
            {picking === p.id ? (
              <>
                <Loader2 size={11} className="animate-spin" />
                Locking in.
              </>
            ) : (
              <>
                Pick this path
                <ChevronRight size={11} strokeWidth={2.5} />
              </>
            )}
          </div>
        </button>
      ))}
    </div>
  </section>
);
